import { useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useSignalR } from "../context/SignalRContext";
import { api } from "../store/api";

/**
 * Custom hook for POS real-time events
 * Handles OrderStatusChanged, PaymentStatusChanged, and PrintJobStatusChanged events
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.onOrderStatusChanged - Callback for order status changes
 * @param {Function} options.onPaymentStatusChanged - Callback for payment status changes
 * @param {Function} options.onPrintJobStatusChanged - Callback for print job status changes
 * @param {boolean} options.autoInvalidate - Auto-invalidate RTK Query cache (default: true)
 */
export const usePosRealtime = (options = {}) => {
  const {
    onOrderStatusChanged,
    onPaymentStatusChanged,
    onPrintJobStatusChanged,
    onOrderChanged,
    onSeatStateChanged,
    onKitchenTicketChanged,
    onCashSessionChanged,
    autoInvalidate = true,
  } = options;

  const { on, isConnected } = useSignalR();
  const dispatch = useDispatch();

  // Handle OrderStatusChanged event
  const handleOrderStatusChanged = useCallback(
    (payload) => {
      console.log("[POS Realtime] OrderStatusChanged:", payload);

      // Invalidate relevant cache tags
      if (autoInvalidate) {
        dispatch(
          api.util.invalidateTags([
            { type: "PosOrder", id: payload.orderId },
            { type: "Seat", id: "LIST" },
          ]),
        );
      }

      // Call custom callback
      if (onOrderStatusChanged) {
        onOrderStatusChanged(payload);
      }
    },
    [dispatch, autoInvalidate, onOrderStatusChanged],
  );

  // Handle PaymentStatusChanged event
  const handlePaymentStatusChanged = useCallback(
    (payload) => {
      console.log("[POS Realtime] PaymentStatusChanged:", payload);

      // Invalidate relevant cache tags
      if (autoInvalidate) {
        dispatch(
          api.util.invalidateTags([
            { type: "PosOrder", id: payload.orderId },
            { type: "Invoice", id: "LIST" },
            { type: "Seat", id: "LIST" },
          ]),
        );
      }

      // Call custom callback
      if (onPaymentStatusChanged) {
        onPaymentStatusChanged(payload);
      }
    },
    [dispatch, autoInvalidate, onPaymentStatusChanged],
  );

  // Handle PrintJobStatusChanged event
  const handlePrintJobStatusChanged = useCallback(
    (payload) => {
      console.log("[POS Realtime] PrintJobStatusChanged:", payload);

      // Call custom callback
      if (onPrintJobStatusChanged) {
        onPrintJobStatusChanged(payload);
      }
    },
    [onPrintJobStatusChanged],
  );

  // Handle rich OrderChanged event
  const handleOrderChanged = useCallback(
    (payload) => {
      console.log("[POS Realtime] OrderChanged:", payload);

      if (autoInvalidate) {
        dispatch(
          api.util.invalidateTags([
            { type: "PosOrder", id: payload.orderId },
            { type: "Seat", id: "LIST" },
            { type: "FulfillmentOrder", id: payload.orderId },
          ]),
        );
      }

      if (onOrderChanged) {
        onOrderChanged(payload);
      }
    },
    [dispatch, autoInvalidate, onOrderChanged],
  );

  // Handle SeatStateChanged event
  const handleSeatStateChanged = useCallback(
    (payload) => {
      console.log("[POS Realtime] SeatStateChanged:", payload);

      if (autoInvalidate) {
        dispatch(api.util.invalidateTags(["Seat"]));
      }

      if (onSeatStateChanged) {
        onSeatStateChanged(payload);
      }
    },
    [dispatch, autoInvalidate, onSeatStateChanged],
  );

  // Handle SeatBoardChanged event
  const handleSeatBoardChanged = useCallback(
    (payload) => {
      console.log("[POS Realtime] SeatBoardChanged:", payload);

      if (autoInvalidate) {
        dispatch(api.util.invalidateTags(["Seat"]));
      }

      if (onSeatStateChanged) {
        onSeatStateChanged(payload);
      }
    },
    [dispatch, autoInvalidate, onSeatStateChanged],
  );

  // Handle KitchenTicketChanged event
  const handleKitchenTicketChanged = useCallback(
    (payload) => {
      console.log("[POS Realtime] KitchenTicketChanged:", payload);

      if (autoInvalidate) {
        dispatch(api.util.invalidateTags(["PosOrder", "FulfillmentOrder"]));
      }

      if (onKitchenTicketChanged) {
        onKitchenTicketChanged(payload);
      }
    },
    [dispatch, autoInvalidate, onKitchenTicketChanged],
  );

  const handleCashSessionChanged = useCallback(
    (payload) => {
      console.log("[POS Realtime] CashSessionChanged:", payload);

      if (autoInvalidate) {
        dispatch(api.util.invalidateTags(["TillSession", "Reconciliation"]));
      }

      if (onCashSessionChanged) {
        onCashSessionChanged(payload);
      }
    },
    [dispatch, autoInvalidate, onCashSessionChanged],
  );

  // Subscribe to events
  useEffect(() => {
    if (!isConnected) return;

    const unsubscribeOrder = on("OrderStatusChanged", handleOrderStatusChanged);
    const unsubscribePayment = on(
      "PaymentStatusChanged",
      handlePaymentStatusChanged,
    );
    const unsubscribePrint = on(
      "PrintJobStatusChanged",
      handlePrintJobStatusChanged,
    );
    const unsubscribeOrderChanged = on("OrderChanged", handleOrderChanged);
    const unsubscribeSeatState = on("SeatStateChanged", handleSeatStateChanged);
    const unsubscribeSeatBoard = on("SeatBoardChanged", handleSeatBoardChanged);
    const unsubscribeSeatBoardLower = on(
      "seatboardchanged",
      handleSeatBoardChanged,
    );
    const unsubscribeKitchenTicket = on(
      "KitchenTicketChanged",
      handleKitchenTicketChanged,
    );
    const unsubscribePaymentChanged = on(
      "PaymentChanged",
      handlePaymentStatusChanged,
    );
    const unsubscribeCashSessionChanged = on(
      "CashSessionChanged",
      handleCashSessionChanged,
    );
    const unsubscribeCashSessionChangedLower = on(
      "cashsessionchanged",
      handleCashSessionChanged,
    );

    return () => {
      unsubscribeOrder();
      unsubscribePayment();
      unsubscribePrint();
      unsubscribeOrderChanged();
      unsubscribeSeatState();
      unsubscribeSeatBoard();
      unsubscribeSeatBoardLower();
      unsubscribeKitchenTicket();
      unsubscribePaymentChanged();
      unsubscribeCashSessionChanged();
      unsubscribeCashSessionChangedLower();
    };
  }, [
    isConnected,
    on,
    handleOrderStatusChanged,
    handlePaymentStatusChanged,
    handlePrintJobStatusChanged,
    handleOrderChanged,
    handleSeatStateChanged,
    handleSeatBoardChanged,
    handleKitchenTicketChanged,
    handleCashSessionChanged,
  ]);

  return {
    isConnected,
  };
};

/**
 * Hook for listening to specific order updates
 * @param {string} orderId - Order ID to listen for
 * @param {Function} onUpdate - Callback when order is updated
 */
export const useOrderRealtime = (orderId, onUpdate) => {
  return usePosRealtime({
    onOrderStatusChanged: (payload) => {
      if (payload.orderId === orderId && onUpdate) {
        onUpdate(payload);
      }
    },
    onPaymentStatusChanged: (payload) => {
      if (payload.orderId === orderId && onUpdate) {
        onUpdate(payload);
      }
    },
  });
};

/**
 * Hook for seat board real-time updates
 * Auto-invalidates seat list on any order/payment changes
 */
export const useSeatBoardRealtime = (onSeatBoardChanged) => {
  return usePosRealtime({
    autoInvalidate: true,
    onSeatStateChanged: onSeatBoardChanged,
  });
};
